import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  getCohortBuilderPath,
  getCohortsPath,
  getProjectsBasePath,
} from '@verantos-dev/core-constants';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';

import { useResetCohortBuilderState } from '../common/useResetCohortBuilderState';

export const useRedirectOnInvalidParams = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageTab = searchParams.get('pageTab');
  const {
    projectId,
    cohortId,
    cohortError,
    cohortVersionId,
    data,
    isCohortSuccess,
    isProjectSuccess,
    latestCohortVersionId,
    projectData,
    projectError,
  } = useCohortBuilderData();
  const { resetCohortBuilderState } = useResetCohortBuilderState();

  const [hasRedirected, setHasRedirected] = useState(false);

  // Redirect to cohorts path if cohort is not found
  useEffect(() => {
    if (hasRedirected) return;

    if (!projectId) {
      console.info('No projectId found. Redirecting to projects base path.');
      setHasRedirected(true);
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (!cohortId) {
      console.info('No cohortId found. Redirecting to cohorts path.');
      setHasRedirected(true);
      navigate(getCohortsPath(projectId));
      resetCohortBuilderState();
      return;
    }

    if (isProjectSuccess && !projectData?.data) {
      console.info('Project data not found. Redirecting to projects base path.');
      setHasRedirected(true);
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (projectError) {
      console.info('Project error encountered. Redirecting to projects base path.');
      navigate(getProjectsBasePath());
      resetCohortBuilderState();
      return;
    }

    if (isCohortSuccess && !data?.data) {
      console.info('Cohort data not found. Redirecting to cohorts path.');
      setHasRedirected(true);
      navigate(getCohortsPath(projectId));
      resetCohortBuilderState();
      return;
    }

    if (cohortError) {
      console.info('Cohort error encountered. Redirecting to cohorts path.');
      setHasRedirected(true);
      navigate(getCohortsPath(projectId));
      resetCohortBuilderState();
      return;
    }

    if (
      isCohortSuccess &&
      data?.data &&
      cohortVersionId !== latestCohortVersionId &&
      latestCohortVersionId
    ) {
      console.info(
        'Cohort version mismatch. Redirecting to cohort builder with latest cohort version.'
      );
      navigate(
        `${getCohortBuilderPath(projectId, cohortId, latestCohortVersionId)}?pageTab=${
          pageTab || 'events'
        }`
      );
      return;
    }
  }, [
    cohortError,
    cohortId,
    cohortVersionId,
    data?.data,
    hasRedirected,
    isCohortSuccess,
    isProjectSuccess,
    latestCohortVersionId,
    navigate,
    pageTab,
    projectData?.data,
    projectError,
    projectId,
    resetCohortBuilderState,
  ]);
};
